import React, { FunctionComponent, useEffect, useState } from 'react';
import { IPublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { b2cDomainHint, consumeTenantPortalExplicitLogout, scopes } from '../../../b2cConfiguration';

interface TenantPortalAuthBoundaryProps {
    instance: IPublicClientApplication;
}

/**
 * Authentication boundary for tenant portal deeplinks (e.g. /claims/{claimId}
 * arriving from Icebar). It routes users to Azure AD through B2C using
 * domain_hint=commonaad:
 *   1. Process any return from a B2C redirect (handleRedirectPromise).
 *   2. If already signed in, hand off to children.
 *   3. Otherwise attempt silent SSO with the domain hint.
 *   4. On interaction required, fall back to a top-level loginRedirect that
 *      preserves the original deeplink via redirectStartPage.
 */
export const TenantPortalAuthBoundary: FunctionComponent<TenantPortalAuthBoundaryProps> = ({
    instance,
    children,
}) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const authenticate = async () => {
            if (consumeTenantPortalExplicitLogout()) {
                globalThis.location.replace(globalThis.location.origin);
                return;
            }

            try {
                // 1. Process return from a B2C redirect (if any).
                await instance.handleRedirectPromise();

                // 2. Already have an account? Hand off to the app.
                if (instance.getAllAccounts().length > 0) {
                    if (isMounted) {
                        setIsAuthenticated(true);
                    }
                    return;
                }

                // 3. Try silent SSO federated to Azure AD.
                await instance.ssoSilent({
                    scopes,
                    extraQueryParameters: { domain_hint: b2cDomainHint },
                });

                if (isMounted) {
                    setIsAuthenticated(true);
                }
            } catch (error) {
                // 4. Interactive fallback: redirect while preserving the deeplink.
                if (error instanceof InteractionRequiredAuthError) {
                    await instance.loginRedirect({
                        scopes,
                        extraQueryParameters: { domain_hint: b2cDomainHint },
                        redirectStartPage: globalThis.location.href,
                    });
                    return;
                }

                // Any other failure (including ssoSilent unable to resolve an
                // account) also requires interactive sign-in.
                await instance.loginRedirect({
                    scopes,
                    extraQueryParameters: { domain_hint: b2cDomainHint },
                    redirectStartPage: globalThis.location.href,
                });
            }
        };

        authenticate();

        return () => {
            isMounted = false;
        };
    }, [instance]);

    if (!isAuthenticated) {
        return null;
    }

    return <>{children}</>;
};

export default TenantPortalAuthBoundary;
